import { Button } from "@/ui-components/ui/button";
import { annotateActiveSpan, withTelemetrySpan } from "@/lib/monitoring/telemetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { CheckCircle } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Context } from "@/util-components/root/context";

const IntegrationsCallback = ({ name }: { name: string }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const [countdown, setCountdown] = useState(5);
  const [isReady, setIsReady] = useState(false);
  const { refetchUser } = useContext(Context);

  useEffect(() => {
    if (error) {
      void withTelemetrySpan(
        "integration.oauth.callback_error",
        "feature.integration.oauth",
        {
          "integration.provider": name.toLowerCase(),
          "integration.success": false,
          "integration.error": error,
        },
        async () => {
          annotateActiveSpan({
            "integration.callback.handled": true,
          });
        },
      );
      // If there is an error, redirect back to integrations page immediately to show the error
      navigate(`/integrations?error=${encodeURIComponent(error)}`);
    }
  }, [error, name, navigate]);

  // Handle countdown and redirect for success case
  useEffect(() => {
    if (error || !isReady) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/integrations");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, error, isReady]);

  // Show success toast when component mounts (only if no error)
  useEffect(() => {
    if (error) {
      return;
    }

    let isMounted = true;

    void (async () => {
      await withTelemetrySpan(
        "integration.oauth.callback_success",
        "feature.integration.oauth",
        {
          "integration.provider": name.toLowerCase(),
          "integration.success": true,
        },
        async () => {
          annotateActiveSpan({
            "integration.callback.handled": true,
          });
          await refetchUser?.();
        },
      );

      if (!isMounted) {
        return;
      }

      toast.success(`${name} Integration erfolgreich verbunden!`);
      setIsReady(true);
    })();

    return () => {
      isMounted = false;
    };
  }, [name, error, refetchUser]);

  const handleManualRedirect = () => {
    navigate("/integrations");
  };

  if (error) {
    return null; // Or a loading spinner while redirecting
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <CardTitle className="text-xl text-green-600">Integration erfolgreich!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">
            Ihre {name} Integration wurde erfolgreich eingerichtet. Sie können jetzt automatisch Posts auf {name}{" "}
            teilen.
          </div>
          <div className="text-sm text-muted-foreground">
            {isReady ? `Weiterleitung zur Übersicht in ${countdown} Sekunden...` : "Synchronisiere deinen Fortschritt..."}
          </div>
          <Button onClick={handleManualRedirect} className="w-full">
            Jetzt zur Übersicht
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsCallback;
